import React, { useEffect, useMemo, useState } from 'react';
import Badge from 'react-bootstrap/Badge';
import Col from 'react-bootstrap/Col';
import ListGroup from 'react-bootstrap/ListGroup';
import Modal from 'react-bootstrap/Modal';
import Row from 'react-bootstrap/Row';
import { useTranslation } from 'react-i18next';
import { FaEnvelopeOpenText } from 'react-icons/fa';
import ReactMarkdown from 'react-markdown';
import gfm from 'remark-gfm';
import styled from 'styled-components';

import { miscActions } from '../../actions';
import preferenceActions from '../../actions/preference.action';
import { uiHelpers } from '../../helpers';
import { correctedStrapiMarkdown } from '../../helpers/ui.helper';
import useDispatch from '../../hooks/useDispatch';
import { usePreferences } from '../../hooks/usePreferences';
import useSelector from '../../hooks/useSelector';
import BlurPlaceholder from '../../modules/FreeUser/BlurPlaceholder';
import FeatureRequiresVIPAlert from '../../modules/FreeUser/FeatureRequiresVIPAlert';
import LoadingImage from './LoadingImage';

const Styled = styled.div`
  min-height: 50vh;

  .post-list-container,
  .post-content-container {
    overflow-y: auto;
    max-height: 50vh;
  }

  .list-group-item {
    padding: 0.5rem;
    cursor: pointer;
  }
  .post-title {
    font-size: 1rem;
    display: block;
  }

  .post-date {
    font-style: italic;
    font-size: 0.7rem;
  }
  .post-unread {
    position: absolute;
    top: 60%;
    right: 0%;
  }
  .badge-container > .badge {
    margin-right: 0.5rem;
  }
`;

const NewsViewer = styled.div`
  overflow: auto;

  h1 {
    font-size: 1.5rem;
  }
  h2 {
    font-size: 1.4rem;
  }
  h3 {
    font-size: 1.3rem;
  }
  h4 {
    font-size: 1.1rem;
  }
  h5 {
    font-size: 1.1rem;
  }
  h6 {
    font-size: 1rem;
  }

  img {
    max-width: 100%;
  }
`;

const NewsListItem = styled.div`
  /* badge should be at right bottom cornor */
  .badge {
    position: absolute;
    right: 0.5rem;
    bottom: 0.5rem;
  }
`;

interface Props {
  show: boolean;
  onHide: () => void;
  disableBackdropClick: boolean;
}

const NoticeModal: React.FC<Props> = ({
  show,
  onHide,
  disableBackdropClick,
}) => {
  const [t, i18n] = useTranslation();

  const { read, unread, loading } = useSelector(state => state.misc.listPosts);
  const { popUpNoticeModal } = usePreferences();
  const posts = useMemo(
    () =>
      read
        ? [...unread, ...read]
            .sort(
              (a, b) => new Date(b.date).valueOf() - new Date(a.date).valueOf()
            )
            .sort((a, b) => b.popup - a.popup)
        : [],
    [read, unread]
  );

  const [pendingReadIds, setPendingReadIds] = useState<Set<string>>(new Set());
  const readIds = useMemo(
    () => (read ? new Set(read.map(post => post['id'])) : new Set()),
    [read]
  );
  const totalReadIds = useMemo(
    () => new Set([...readIds, ...pendingReadIds]),
    [pendingReadIds, readIds]
  );

  const [activePostIndex, setActivePostIndex] = useState(0);
  const dispatch = useDispatch();

  const activePostId = useMemo(
    () => (show && posts[activePostIndex] ? posts[activePostIndex].id : null),
    [activePostIndex, posts, show]
  );

  useEffect(() => {
    if (activePostId !== null && !totalReadIds.has(activePostId)) {
      dispatch(miscActions.readPost(activePostId));
    }
  }, [activePostId, dispatch, totalReadIds]);

  useEffect(() => {
    if (activePostId !== null) {
      setPendingReadIds(readIds => new Set([...readIds, activePostId]));
    }
  }, [activePostId, dispatch]);

  // if posts are empty, opening notification won't trigger a fresh from readPost,
  // trigger refresh manually!
  useEffect(() => {
    if (posts.length === 0) {
      dispatch(miscActions.listPosts());
    }
  }, [dispatch, posts.length]);

  const extraModalProps: { backdrop?: 'static' } = disableBackdropClick
    ? { backdrop: 'static' }
    : {};

  return (
    <Modal show={show} size="xl" centered onHide={onHide} {...extraModalProps}>
      <Modal.Header closeButton>
        <Modal.Title>
          <FaEnvelopeOpenText /> {t('notification-center')}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Styled>
          <Row>
            <Col className="post-list-container" sm={4}>
              {posts.length === 0 ? (
                <>
                  {loading ? (
                    <LoadingImage loading displayAsBlock />
                  ) : (
                    t('Empty')
                  )}
                </>
              ) : (
                <ListGroup>
                  {posts.map((post, i) => (
                    <ListGroup.Item
                      key={i}
                      active={activePostIndex === i}
                      onClick={() => {
                        setActivePostIndex(i);
                      }}
                    >
                      <NewsListItem>
                        <span className="post-title">
                          {post.content.title[i18n.language]}
                        </span>
                        <span className="post-date">
                          {uiHelpers.formatDate(post.date)}
                        </span>
                        {totalReadIds.has(post.id) ? (
                          ''
                        ) : (
                          <div className="post-unread">
                            <Badge bg="danger"> {t(`Unread`)}</Badge>
                          </div>
                        )}
                        <Badge bg="secondary">
                          {t(`post-category-${post.category}`)}
                        </Badge>
                      </NewsListItem>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </Col>
            <Col className="post-content-container" sm={8}>
              {posts && posts[activePostIndex] && (
                <div>
                  <BlurPlaceholder
                    featureKey="trade-idea"
                    showContent={!posts[activePostIndex].vipOnly}
                  >
                    <NewsViewer>
                      <div className="badge-container">
                        <Badge bg="dark">
                          {t(
                            `post-category-${posts[activePostIndex].category}`
                          )}
                        </Badge>
                        {!posts[activePostIndex].released && (
                          <Badge bg="dark">{t('Unreleased')}</Badge>
                        )}
                      </div>
                      <ReactMarkdown
                        linkTarget="_blank"
                        plugins={[gfm]}
                        source={correctedStrapiMarkdown(
                          posts[activePostIndex].content.body[i18n.language]
                        )}
                      />
                    </NewsViewer>
                  </BlurPlaceholder>
                  {posts[activePostIndex].vipOnly && (
                    <FeatureRequiresVIPAlert
                      featureTitle={t('feature-VIPTradeIdeas')}
                      delayPopup
                    />
                  )}
                </div>
              )}
            </Col>
          </Row>
          <div style={{ textAlign: 'right' }}>
            <input
              type="checkbox"
              name="popUpNoticeModal"
              checked={!popUpNoticeModal}
              onChange={() =>
                dispatch(
                  preferenceActions.setPopUpNoticeModal(!popUpNoticeModal)
                )
              }
            />{' '}
            {t('notification-popup-checkbox')}
          </div>
        </Styled>
      </Modal.Body>
    </Modal>
  );
};

export default NoticeModal;
